import React from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';
import SkipToContent from '../components/SkipToContent';
import OtherStates from '../components/OtherStates';

interface ProfessionInfo {
  id: string;
  name: string;
  icon: string;
  credits: number;
  cycleYears: number;
  terminology: string;
  highlights: string[];
  stateBoard: string;
  boardUrl: string;
}

const DCLicenseRenewal: React.FC = () => {
  const professions: ProfessionInfo[] = [
    {
      id: 'ARCH',
      name: 'Architect',
      icon: '🏛️',
      credits: 24,
      cycleYears: 2,
      terminology: 'LU',
      highlights: [
        '24 Learning Units per 2-year cycle',
        'AIA-CES accredited providers accepted',
        'HSW (Health, Safety & Welfare) topics required',
        'DC Board covers architecture, interior design & landscape architecture',
      ],
      stateBoard: 'DC Board of Architecture, Interior Design, and Landscape Architecture',
      boardUrl: 'https://dchealth.dc.gov/service/board-architecture-interior-design-and-landscape-architecture',
    },
    {
      id: 'PE',
      name: 'Professional Engineer',
      icon: '⚙️',
      credits: 0,
      cycleYears: 2,
      terminology: 'PDH',
      highlights: [
        'DC does not mandate CE for PE license renewal',
        'Voluntary CE strongly encouraged',
        'License renews biennially',
        'NCEES model law not adopted for CE in DC',
      ],
      stateBoard: 'DC Board of Professional Engineering',
      boardUrl: 'https://dchealth.dc.gov/service/board-professional-engineering',
    },
    {
      id: 'CPA',
      name: 'Certified Public Accountant',
      icon: '💼',
      credits: 80,
      cycleYears: 2,
      terminology: 'CPE',
      highlights: [
        '80 CPE hours per 2-year reporting period',
        '4 hours Ethics required each period',
        'Minimum 20 hours per calendar year',
        'NASBA-approved sponsors preferred',
      ],
      stateBoard: 'DC Board of Accountancy',
      boardUrl: 'https://dchealth.dc.gov/service/board-accountancy',
    },
    {
      id: 'ATT',
      name: 'Attorney',
      icon: '⚖️',
      credits: 12,
      cycleYears: 1,
      terminology: 'CLE credits',
      highlights: [
        '12 CLE credits per year',
        '4 hours Legal Ethics and Professional Responsibility',
        'DC Court of Appeals CLE rules apply',
        'Approved live and on-demand programs accepted',
      ],
      stateBoard: 'DC Court of Appeals — CLE Board',
      boardUrl: 'https://www.dccourts.gov/court-of-appeals/mcle',
    },
    {
      id: 'RN',
      name: 'Registered Nurse',
      icon: '⚕️',
      credits: 24,
      cycleYears: 2,
      terminology: 'CE hours',
      highlights: [
        '24 CE contact hours per 2-year renewal period',
        'DC Board of Nursing approved providers',
        'License renews biennially on June 30',
        'CE must relate to area of nursing practice',
      ],
      stateBoard: 'DC Board of Nursing',
      boardUrl: 'https://dchealth.dc.gov/service/board-nursing',
    },
    {
      id: 'PHARM',
      name: 'Pharmacist',
      icon: '💊',
      credits: 30,
      cycleYears: 2,
      terminology: 'CE hours',
      highlights: [
        '30 CE hours per 2-year cycle',
        '3 hours Law required',
        'ACPE-accredited CE providers required',
        'License renews biennially on June 30',
      ],
      stateBoard: 'DC Board of Pharmacy',
      boardUrl: 'https://dchealth.dc.gov/service/board-pharmacy',
    },
  ];

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Washington, D.C. Professional License Renewal Requirements',
      description: 'Complete guide to Washington, D.C. professional license renewal CE requirements for architects, CPAs, attorneys, nurses, and pharmacists.',
      url: 'https://cectrack.com/dc-license-renewal',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://cectrack.com' },
          { '@type': 'ListItem', position: 2, name: 'Washington DC License Renewal', item: 'https://cectrack.com/dc-license-renewal' },
        ],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How many CE hours do I need in Washington, D.C.?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Washington, D.C. CE requirements vary by profession. Architects need 24 LU biennially, professional engineers have no mandatory CE requirement, CPAs need 80 CPE hours every 2 years (including 4 ethics hours), attorneys need 12 CLE credits per year (including 4 legal ethics hours), RNs need 24 CE hours biennially, and pharmacists need 30 CE hours biennially.',
          },
        },
        {
          '@type': 'Question',
          name: 'Which agency handles professional licenses in Washington, D.C.?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Most Washington, D.C. professional licenses are regulated by the DC Department of Health (DCDOH), which oversees individual licensing boards for architects, CPAs, nurses, and pharmacists. The DC Court of Appeals oversees attorney CLE requirements.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I use online courses for Washington DC CE requirements?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, most DC professions accept online CE courses from accredited providers. Attorneys may use live or approved on-demand CLE programs. Pharmacists must use ACPE-accredited providers. DC CPAs should use NASBA-approved sponsors.',
          },
        },
      ],
    },
  ];

  return (
    <>
      <SkipToContent />
      <SEOHelmet
        title="Washington DC Professional License Renewal | CE Requirements & Deadlines"
        description="Complete guide to Washington, D.C. professional license renewal CE requirements for architects, CPAs, attorneys, nurses, and more. Track all your DC CE credits in one place."
        keywords="Washington DC license renewal, DC CE requirements, District of Columbia continuing education, DC professional licenses, DC architects, DC CPAs, Washington DC attorneys"
        canonical="https://cectrack.com/dc-license-renewal"
        ogImage="https://cectrack.com/og-image.png"
        structuredData={structuredData}
      />

      <section aria-label="Page header" style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', color: 'white', padding: '80px 0 60px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")", opacity: 0.4 }} />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '1.5rem', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>Washington, D.C. Professional License Renewal</h1>
              <p style={{ fontSize: '1.3rem', marginBottom: '2rem', opacity: 0.95 }}>Track your continuing education requirements across all your Washington, D.C. professional licenses in one place.</p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link to="/register" className="btn btn-light btn-lg px-5 py-3" style={{ fontWeight: '600', borderRadius: '8px' }}>Start Tracking Free</Link>
                <a href="#professions" className="btn btn-outline-light btn-lg px-5 py-3" style={{ fontWeight: '600', borderRadius: '8px' }}>View Requirements</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section aria-label="Statistics" style={{ padding: '60px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row g-4 text-center">
            <div className="col-md-3"><div className="p-4"><div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>6</div><div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Professions Supported</div></div></div>
            <div className="col-md-3"><div className="p-4"><div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>50+</div><div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Category Requirements</div></div></div>
            <div className="col-md-3"><div className="p-4"><div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>100%</div><div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Board Compliant</div></div></div>
            <div className="col-md-3"><div className="p-4"><div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>24/7</div><div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Access Anywhere</div></div></div>
          </div>
        </div>
      </section>

      <main id="main-content">
        <section id="professions" aria-labelledby="professions-heading" style={{ padding: '80px 0' }}>
          <div className="container">
            <div className="text-center mb-5"><h2 id="professions-heading" style={{ fontSize: '2.5rem', fontWeight: '700' }}>Washington, D.C. CE Requirements by Profession</h2></div>
            <div className="row g-4">
              {professions.map((prof) => (
                <div key={prof.id} className="col-md-6 col-lg-4">
                  <div className="card h-100 border-0 shadow-sm" style={{ transition: 'all 0.3s ease', cursor: 'pointer' }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'; }}>
                    <div className="card-body p-4">
                      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{prof.icon}</div>
                      <h5 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '1rem' }}>{prof.name}</h5>
                      <div style={{ backgroundColor: '#e7f3ff', padding: '12px 16px', borderRadius: '8px', marginBottom: '1rem' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e3a8a' }}>{prof.credits > 0 ? `${prof.credits} ${prof.terminology}` : 'No CE Required'}</div>
                        <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>{prof.cycleYears === 1 ? 'Annual' : prof.cycleYears === 2 ? 'Biennial' : 'Triennial'} Renewal</div>
                      </div>
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6c757d', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Key Requirements:</div>
                        <ul style={{ fontSize: '0.9rem', margin: 0, paddingLeft: '1.2rem' }}>{prof.highlights.map((h, i) => <li key={i} style={{ marginBottom: '0.3rem' }}>{h}</li>)}</ul>
                      </div>
                      <div style={{ paddingTop: '1rem', borderTop: '1px solid #e9ecef', fontSize: '0.85rem', color: '#6c757d' }}>
                        <strong>{prof.stateBoard}</strong><br />
                        <a href={prof.boardUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'none' }}>Visit Board →</a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6 mb-4 mb-lg-0">
                <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>Simplify Your Washington, D.C. License Management</h2>
                <p style={{ fontSize: '1.1rem', color: '#6c757d', marginBottom: '2rem' }}>CEC Track automatically tracks requirements, calculates progress, and sends reminders so you never miss a DC renewal deadline.</p>
                <ul style={{ fontSize: '1.1rem', listStyle: 'none', padding: 0 }}>
                  {['Automated deadline tracking', 'Upload certificates to requirements', 'Real-time progress by category', 'Multi-jurisdiction support', 'Secure cloud storage', 'Email notifications'].map((f, i) => (
                    <li key={i} style={{ marginBottom: '1rem', paddingLeft: '2rem', position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 0, color: '#10b981', fontSize: '1.5rem' }}>✓</span>{f}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="col-lg-6">
                <div className="p-5 text-center" style={{ backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}>
                  <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📱</div>
                  <h3 style={{ fontSize: '1.8rem', fontWeight: '700', marginBottom: '1rem' }}>Start Tracking Today</h3>
                  <p style={{ color: '#6c757d', marginBottom: '2rem' }}>Join professionals in Washington, D.C. who trust CEC Track.</p>
                  <Link to="/register" className="btn btn-primary btn-lg px-5 py-3" style={{ fontWeight: '600', background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', border: 'none', borderRadius: '8px' }}>Create Free Account</Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section aria-labelledby="resources-heading" style={{ padding: '80px 0' }}>
          <div className="container">
            <div className="text-center mb-5"><h2 id="resources-heading" style={{ fontSize: '2.5rem', fontWeight: '700' }}>Washington, D.C. Licensing Resources</h2></div>
            <div className="row g-4">
              <div className="col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <h4 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>DC Department of Health — Health Regulation</h4>
                    <p style={{ color: '#6c757d', marginBottom: '1rem' }}>Administers professional licensing boards in Washington, D.C. including architects, CPAs, nurses, and pharmacists.</p>
                    <div style={{ marginBottom: '0.5rem' }}><strong>Phone:</strong> (877) 672-2174</div>
                    <div style={{ marginBottom: '0.5rem' }}><strong>Website:</strong> <a href="https://dchealth.dc.gov" target="_blank" rel="noopener noreferrer">dchealth.dc.gov</a></div>
                    <div style={{ marginBottom: '1rem' }}><strong>Address:</strong> 899 North Capitol St. NE, Washington, DC 20002</div>
                    <a href="https://dchealth.dc.gov/service/health-professional-licensing" target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary">Professional Licensing</a>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <h4 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>DC Court of Appeals — CLE Board</h4>
                    <p style={{ color: '#6c757d', marginBottom: '1rem' }}>Administers Mandatory Continuing Legal Education for DC attorneys, including a high 4-hour ethics and professional responsibility requirement.</p>
                    <div style={{ marginBottom: '0.5rem' }}><strong>Phone:</strong> (202) 879-2709</div>
                    <div style={{ marginBottom: '0.5rem' }}><strong>Website:</strong> <a href="https://www.dccourts.gov/court-of-appeals/mcle" target="_blank" rel="noopener noreferrer">dccourts.gov/court-of-appeals/mcle</a></div>
                    <div style={{ marginBottom: '1rem' }}><strong>Address:</strong> 430 E Street NW, Washington, DC 20001</div>
                    <a href="https://www.dccourts.gov/court-of-appeals/mcle" target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary">MCLE Requirements</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <OtherStates currentState="DC" />

        <section aria-label="Call to action" style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', color: 'white', padding: '80px 0' }}>
          <div className="container text-center">
            <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>Ready to Simplify Your Washington, D.C. License Renewal?</h2>
            <p style={{ fontSize: '1.25rem', marginBottom: '2rem', opacity: 0.95 }}>Join professionals in Washington, D.C. who never worry about missing CE requirements</p>
            <Link to="/register" className="btn btn-light btn-lg px-5 py-3" style={{ fontWeight: '600', borderRadius: '8px' }}>Get Started Free</Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default DCLicenseRenewal;
